import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Plane,
  Building2,
  Phone,
  Hotel,
  Sparkles,
  BadgeDollarSign,
  TrendingUp,
  Mail,
  LifeBuoy,
  Check,
  X,
  Clock,
  QrCode,
  FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdminRecordModal from "../components/AdminRecordModal";
import IntegrationStatusCard from "../components/IntegrationStatusCard";

/* ------------------------------------------------------------------ */
/* Members segmentation card                                          */
/* ------------------------------------------------------------------ */
function MembersSegmentationCard() {
  const [seg, setSeg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/users/segmented")
      .then((r) => {
        if (!cancelled) setSeg(r.data);
      })
      .catch(() => {
        /* silent — dashboard still functional without this widget */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const c = seg?.counts || {};
  const chips = [
    { key: "sign_ups", label: "Sign-ups", value: c.sign_ups, tint: "text-[var(--gold)]" },
    { key: "paying", label: "Paying", value: c.paying, tint: "text-[var(--emerald)]" },
    { key: "jewels", label: "Jewels", value: c.jewels, tint: "text-[var(--brand-red)]" },
    { key: "hosts", label: "Hosts", value: c.hosts, tint: "text-[var(--gold)]" },
    { key: "all", label: "All", value: c.total, tint: "text-[var(--emerald)]" },
  ];

  return (
    <div
      className="lux-card mt-10 border border-[var(--gold)]/40"
      data-testid="admin-members-segmentation-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Members segmentation</div>
          <h3 className="font-display text-2xl text-[var(--emerald)] mt-1">
            Sign-ups · Members · Paying · Jewels
          </h3>
          <p className="text-xs text-[var(--muted)] mt-1 max-w-lg">
            Deep-dive into every user segment — filter by tier, search, and export.
          </p>
        </div>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors px-3 py-2"
          data-testid="admin-members-open-deepdive"
        >
          Open deep-dive →
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
        {chips.map((chip) => (
          <Link
            key={chip.key}
            to={`/admin/users?tab=${chip.key}`}
            data-testid={`admin-seg-chip-${chip.key}`}
            className="border border-[var(--emerald)]/15 px-3 py-3 hover:border-[var(--gold)] transition-colors"
          >
            <div className="overline">{chip.label}</div>
            <div className={`font-display text-3xl tabular-nums mt-1 ${chip.tint}`}>
              {chip.value ?? "—"}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stat card                                                          */
/* ------------------------------------------------------------------ */
function Stat({ icon: Icon, label, value, sub, to }) {
  const inner = (
    <div className="lux-card flex items-start gap-4 transition-all hover:-translate-y-0.5">
      <div className="w-11 h-11 border border-[var(--gold)]/40 flex items-center justify-center text-[var(--gold)] shrink-0">
        <Icon size={18} strokeWidth={1.25} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="overline">{label}</div>
        <div className="font-display text-3xl text-[var(--emerald)] mt-1 tabular-nums">
          {value ?? "—"}
        </div>
        {sub && <div className="text-[11px] text-[var(--muted)] mt-1">{sub}</div>}
        {to && <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-red)] mt-2">Manage →</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} data-testid={`stat-link-${label.replace(/\s+/g,"-").toLowerCase()}`}>{inner}</Link> : inner;
}

function Section({ title, overline, children, exportSlug }) {
  const handleExport = () => {
    if (!exportSlug) return;
    const url = `${API}/admin/export/${exportSlug}`;
    // Use fetch with auth header to download (admin token in localStorage)
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("sasha_token")}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        return r.blob();
      })
      .then((blob) => {
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        const filename = `jante-${exportSlug}-${today}.csv`;
        const link = document.createElement("a");
        const obj = window.URL.createObjectURL(blob);
        link.href = obj;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(obj);
        toast.success(`${exportSlug} exported.`);
      })
      .catch((e) => toast.error(e.message || "Export failed."));
  };

  return (
    <section className="mt-14">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">{overline}</div>
          <h2 className="font-display text-3xl text-[var(--emerald)] mt-2">{title}</h2>
        </div>
        {exportSlug && (
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors px-3 py-2"
            data-testid={`section-export-${exportSlug}`}
            title="Download all rows for this section as a CSV (opens directly in Excel/Numbers)."
          >
            <FileSpreadsheet size={12} strokeWidth={1.5} /> Export · Excel
          </button>
        )}
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

function Table({ rows, columns, empty, onView, viewCollection, viewLabel }) {
  if (!rows || rows.length === 0) {
    return <div className="lux-card text-center text-sm text-[var(--muted)]">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--emerald)]/10">
            {columns.map((c) => (
              <th key={c.key} className="text-left px-3 py-2 font-normal">
                {c.label}
              </th>
            ))}
            {onView && <th className="text-left px-3 py-2 font-normal w-12">View</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id || r.confirmation_number || r.recall_code || i}
              className={`border-b border-[var(--emerald)]/5 hover:bg-[var(--ivory-2)]/60 ${onView && r.id ? "cursor-pointer" : ""}`}
              onClick={() => {
                if (onView && r.id) onView({ collection: viewCollection, id: r.id, title: viewLabel });
              }}
              data-testid={onView && r.id ? `admin-row-${viewCollection}-${r.id}` : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-3 align-top text-[var(--ink)]">
                  {c.render ? c.render(r) : fmt(r[c.key])}
                </td>
              ))}
              {onView && (
                <td className="px-3 py-3 align-top">
                  {r.id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onView({ collection: viewCollection, id: r.id, title: viewLabel });
                      }}
                      className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] hover:text-[var(--emerald)] underline"
                      data-testid={`admin-row-view-${viewCollection}-${r.id}`}
                    >
                      View
                    </button>
                  ) : (
                    <span className="text-[10px] text-[var(--muted)]">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [apps, setApps] = useState([]);
  const [appsBusy, setAppsBusy] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  // The "View details" modal target — null when closed.
  const [viewTarget, setViewTarget] = useState(null);
  const openRecord = useCallback((target) => setViewTarget(target), []);

  const loadApps = useCallback(async () => {
    try {
      const r = await api.get("/admin/host-applications");
      setApps(r.data.applications || []);
    } catch (e) {
      console.error("host-applications failed", e);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/admin");
      return;
    }
    if (user.role !== "admin") {
      setErr("Admin access required.");
      return;
    }
    api
      .get("/admin/dashboard/summary")
      .then((r) => setData(r.data))
      .catch((e) => {
        console.error("admin dashboard failed", e);
        setErr(e?.response?.data?.detail || "Could not load dashboard.");
      });
    loadApps();
  }, [user, authLoading, navigate, loadApps]);

  const decide = async (appId, action) => {
    const note = window.prompt(
      action === "approve"
        ? "Optional personal note to the applicant (shown in the approval email):"
        : "Optional polite reason (shown in the decline email):",
      "",
    );
    if (note === null) return; // cancelled
    setAppsBusy(true);
    setPendingId(appId);
    try {
      const r = await api.post(`/admin/host-applications/${appId}/${action}`, { message: note });
      if (action === "approve") {
        const mn = r.data?.host_member_number;
        toast.success(
          mn ? `Approved — host member no. ${mn} issued.` : "Approved — applicant notified.",
        );
      } else {
        toast.success("Declined — applicant notified.");
      }
      await loadApps();
      // Refresh dashboard summary so the "approved" counters update
      try {
        const s = await api.get("/admin/dashboard/summary");
        setData(s.data);
      } catch (_) {
        /* ignore */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update application.");
    } finally {
      setAppsBusy(false);
      setPendingId(null);
    }
  };

  if (err) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="admin-forbidden">
        <LifeBuoy size={32} strokeWidth={1.25} className="text-[var(--burgundy)] mx-auto" />
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">Admins only.</h1>
        <p className="text-[var(--ink-2)] mt-4">{err}</p>
        <button onClick={() => navigate("/")} className="btn-primary mt-6">
          Back to Sasha
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-20 text-[var(--muted)]">Loading dashboard…</div>
    );
  }

  const { counts, last_30_days, tier_breakdown, recent } = data;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14" data-testid="admin-dashboard">
      <div className="overline">Janté HQ · Admin view</div>
      <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">Operations dashboard</h1>
      <p className="text-[var(--ink-2)] mt-3 max-w-xl text-sm">
        Every concierge transfer, itinerary, host application, and add-on request at a glance.
        Updated live.
      </p>

      {/* Quick links — staff tools (QR, legal, etc.) */}
      <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-quick-links">
        <Link
          to="/qr"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors px-3 py-2"
          data-testid="admin-qr-link"
        >
          <QrCode size={12} strokeWidth={1.5} /> Brand QR · download
        </Link>
      </div>

      <IntegrationStatusCard />

      {/* Top counters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="admin-counters">
        <Stat
          icon={Users}
          label="Total members"
          value={counts.users}
          sub={`${counts.clients} client · ${counts.hosts} host`}
        />
        <Stat
          icon={Plane}
          label="Itineraries"
          value={counts.itineraries}
          sub={`+${last_30_days.new_itineraries} in 30 days`}
        />
        <Stat
          icon={Phone}
          label="Concierge transfers"
          value={counts.concierge_requests}
          sub={`+${last_30_days.concierge_requests} in 30 days`}
        />
        <Stat
          icon={BadgeDollarSign}
          label="Add-on requests"
          value={counts.add_on_requests}
          sub={`+${last_30_days.add_on_requests} in 30 days`}
        />
        <Stat
          icon={Hotel}
          label="Listings"
          value={counts.accommodations}
          sub={`${counts.accommodations_live} live`}
          to="/admin/listings"
        />
        <Stat
          icon={Building2}
          label="Host applications"
          value={counts.host_applications}
          sub={`+${last_30_days.host_applications} in 30 days`}
        />
        <Stat
          icon={Sparkles}
          label="Offer claims"
          value={counts.itinerary_offer_claims}
          sub="email · membership · concierge"
        />
        <Stat
          icon={Mail}
          label="Marketing consents"
          value={counts.marketing_consents}
          sub="site-entry opt-ins"
        />
        <Stat
          icon={LifeBuoy}
          label="Guest contacts"
          value={counts.guest_contacts || 0}
          sub="Properties page inbound"
        />
        <Stat
          icon={Sparkles}
          label="Jewel Store bookings"
          value={counts.jewel_store_bookings || 0}
          sub="à-la-carte add-ons"
        />
      </div>

      {/* Members segmentation deep-dive card */}
      <MembersSegmentationCard />

      {/* Membership tier breakdown */}
      <Section overline="Club Janté · client mix" title="Who's in which tier" exportSlug="users">
        <div className="lux-card">
          {tier_breakdown.length === 0 ? (
            <div className="text-sm text-[var(--muted)]">No members yet.</div>
          ) : (
            <ul className="divide-y divide-[var(--emerald)]/10">
              {tier_breakdown.map((t) => (
                <li
                  key={t.tier}
                  className="py-2 flex items-center justify-between"
                  data-testid={`tier-row-${t.tier}`}
                >
                  <span className="capitalize text-[var(--emerald)]">{t.tier}</span>
                  <span className="font-display text-2xl tabular-nums text-[var(--emerald)]">
                    {t.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      {/* Recent activity tables */}
      <Section overline="Concierge transfers" title="Latest handoffs" exportSlug="concierge_requests">
        <Table
          rows={recent.concierge_requests}
          columns={[
            { key: "confirmation_number", label: "Conf #" },
            { key: "name", label: "Guest" },
            { key: "email", label: "Email" },
            { key: "trip_ref", label: "Trip ref" },
            { key: "status", label: "Status" },
            {
              key: "created_at",
              label: "Created",
              render: (r) => fmtDate(r.created_at),
            },
            {
              key: "expires_at",
              label: "Expires",
              render: (r) => fmtDate(r.expires_at),
            },
          ]}
          empty="No concierge transfers yet."
          onView={openRecord}
          viewCollection="concierge_requests"
          viewLabel="Concierge transfer"
        />
      </Section>

      <Section overline="Itinerary-offer choices" title="Sasha's 3-option chooser" exportSlug="itinerary_offer_claims">
        <Table
          rows={recent.offer_claims}
          columns={[
            { key: "option", label: "Picked" },
            { key: "tier_id", label: "Tier" },
            { key: "name", label: "Guest" },
            { key: "email", label: "Email" },
            { key: "trip_ref", label: "Trip ref" },
            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
          ]}
          empty="No offer claims yet."
          onView={openRecord}
          viewCollection="itinerary_offer_claims"
          viewLabel="Offer claim"
        />
      </Section>

      <Section overline="Inclusive add-ons" title="Chef · grocery · housekeeping · more" exportSlug="add_on_requests">
        <Table
          rows={recent.add_on_requests}
          columns={[
            { key: "name", label: "Guest" },
            { key: "email", label: "Email" },
            { key: "trip_ref", label: "Trip ref" },
            {
              key: "services",
              label: "Services",
              render: (r) => (r.services || []).join(", ") || "—",
            },
            {
              key: "estimate_usd",
              label: "Estimate",
              render: (r) => (r.estimate_usd ? `$${Number(r.estimate_usd).toLocaleString()}` : "—"),
            },
            {
              key: "start_date",
              label: "Dates",
              render: (r) => `${fmtDate(r.start_date)} → ${fmtDate(r.end_date)}`,
            },
          ]}
          empty="No add-on requests yet."
          onView={openRecord}
          viewCollection="add_on_requests"
          viewLabel="Add-on request"
        />
      </Section>

      <Section overline="Host applications" title="New inquiries from hosts" exportSlug="host_applications">
        <HostApplicationQueue
          apps={apps}
          busy={appsBusy}
          pendingId={pendingId}
          onDecide={decide}
          onView={openRecord}
        />
      </Section>

      <Section overline="Guest contacts" title="Properties-page inbound" exportSlug="guest_contacts">
        <Table
          rows={recent.guest_contacts || []}
          columns={[
            { key: "contact_type", label: "Type" },
            { key: "name", label: "Guest" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "property_name", label: "Property" },
            {
              key: "is_emergency",
              label: "Urgent",
              render: (r) =>
                r.is_emergency ? <span className="text-[var(--brand-red)] font-semibold">URGENT</span> : "—",
            },
            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
          ]}
          empty="No guest contacts yet."
          onView={openRecord}
          viewCollection="guest_contacts"
          viewLabel="Guest contact"
        />
      </Section>

      <Section overline="Jewel Store" title="À-la-carte add-on bookings" exportSlug="jewel_store_bookings">
        <Table
          rows={recent.jewel_store_bookings || []}
          columns={[
            { key: "confirmation_number", label: "Conf #" },
            { key: "name", label: "Guest" },
            { key: "email", label: "Email" },
            { key: "product_name", label: "Service" },
            { key: "city_name", label: "City" },
            { key: "start_date", label: "Start", render: (r) => fmtDate(r.start_date) },
            { key: "created_at", label: "Booked", render: (r) => fmtDate(r.created_at) },
          ]}
          empty="No Jewel Store bookings yet."
          onView={openRecord}
          viewCollection="jewel_store_bookings"
          viewLabel="Jewel Store booking"
        />
      </Section>

      <Section overline="Itineraries saved" title="Recently-saved trips" exportSlug="itineraries">
        <Table
          rows={recent.itineraries}
          columns={[
            { key: "recall_code", label: "Trip ref" },
            { key: "destination", label: "Destination" },
            { key: "title", label: "Title" },
            { key: "created_at", label: "Saved", render: (r) => fmtDate(r.created_at) },
          ]}
          empty="No saved itineraries yet."
          onView={openRecord}
          viewCollection="itineraries"
          viewLabel="Itinerary"
        />
      </Section>

      <div className="mt-16 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] flex items-center gap-2">
        <TrendingUp size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
        Tap any row to open the full record.
      </div>

      <AdminRecordModal
        open={!!viewTarget}
        onClose={() => setViewTarget(null)}
        collection={viewTarget?.collection}
        recordId={viewTarget?.id}
        title={viewTarget?.title}
      />
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Host Application Queue — approve / decline controls                */
/* ------------------------------------------------------------------ */
function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pending", bg: "bg-[var(--gold)]/20", color: "text-[var(--gold)]", icon: Clock },
    approved: { label: "Approved", bg: "bg-[var(--emerald)]/15", color: "text-[var(--emerald)]", icon: Check },
    declined: { label: "Declined", bg: "bg-[var(--brand-red)]/15", color: "text-[var(--brand-red)]", icon: X },
    new: { label: "Pending", bg: "bg-[var(--gold)]/20", color: "text-[var(--gold)]", icon: Clock },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${cfg.bg} ${cfg.color}`}
      data-testid={`status-badge-${status || "pending"}`}
    >
      <Icon size={10} strokeWidth={1.75} />
      {cfg.label}
    </span>
  );
}

function HostApplicationQueue({ apps, busy, pendingId, onDecide, onView }) {
  if (!apps || apps.length === 0) {
    return <div className="lux-card text-center text-sm text-[var(--muted)]">No host applications yet.</div>;
  }
  return (
    <div className="space-y-4" data-testid="host-application-queue">
      {apps.map((a) => {
        const s = a.status || "pending";
        const isActionable = s !== "approved" && s !== "declined";
        const loading = busy && pendingId === a.id;
        return (
          <div
            key={a.id}
            className="lux-card flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6"
            data-testid={`host-app-row-${a.application_number || a.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => onView && onView({ collection: "host_applications", id: a.id, title: `Application · ${a.application_number || ""}` })}
                  className="font-display text-lg text-[var(--emerald)] hover:text-[var(--brand-red)] underline-offset-4 hover:underline"
                  data-testid="host-app-number"
                >
                  {a.application_number || a.id.slice(0, 8)}
                </button>
                <StatusBadge status={s} />
                {a.host_member_number && (
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]" data-testid="host-app-member-number">
                    Host · {a.host_member_number}
                  </span>
                )}
              </div>
              <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[var(--ink-2)]">
                <div>
                  <span className="overline inline-block mr-2">Name</span>
                  {a.name || "—"}
                </div>
                <div>
                  <span className="overline inline-block mr-2">Email</span>
                  {a.email || "—"}
                </div>
                <div>
                  <span className="overline inline-block mr-2">Program</span>
                  {a.program || "—"}
                  {a.tier_id ? ` · ${a.tier_id}` : ""}
                </div>
                <div>
                  <span className="overline inline-block mr-2">Phone</span>
                  {a.phone || "—"}
                </div>
                <div className="sm:col-span-2">
                  <span className="overline inline-block mr-2">Property</span>
                  {a.property_address || "—"}
                </div>
                {a.property_details && (
                  <div className="sm:col-span-2 text-xs italic text-[var(--ink-2)]/80">
                    {a.property_details}
                  </div>
                )}
              </div>
              {a.admin_note && (
                <div className="mt-3 text-xs text-[var(--muted)] border-l-2 border-[var(--gold)] pl-3">
                  Admin note: {a.admin_note}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0" data-testid="host-app-actions">
              <button
                onClick={() => onView && onView({ collection: "host_applications", id: a.id, title: `Application · ${a.application_number || ""}` })}
                className="px-4 py-2 border border-[var(--gold)] text-[var(--emerald)] text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--gold)] hover:text-[var(--ivory)] transition-colors"
                data-testid="host-app-view-btn"
              >
                View full
              </button>
              {isActionable ? (
                <>
                  <button
                    onClick={() => onDecide(a.id, "approve")}
                    disabled={loading}
                    className="px-4 py-2 bg-[var(--emerald)] text-[var(--ivory)] text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--emerald)]/90 transition-colors disabled:opacity-50"
                    data-testid="host-app-approve-btn"
                  >
                    {loading ? "Working…" : "Approve"}
                  </button>
                  <button
                    onClick={() => onDecide(a.id, "decline")}
                    disabled={loading}
                    className="px-4 py-2 border border-[var(--brand-red)] text-[var(--brand-red)] text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--brand-red)] hover:text-[var(--ivory)] transition-colors disabled:opacity-50"
                    data-testid="host-app-decline-btn"
                  >
                    Decline
                  </button>
                </>
              ) : (
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
                  Decision recorded
                  {a.approved_at ? ` · ${new Date(a.approved_at).toLocaleDateString()}` : ""}
                  {a.declined_at ? ` · ${new Date(a.declined_at).toLocaleDateString()}` : ""}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
